/// ProcessList: Shows deployed BPMN processes and Node flows with versions,
/// instance counts, and expandable instance lists.

import { useState } from "react";
import { ChevronRight, GitBranch, FileCode, Play } from "lucide-react";
import { useEngineStore } from "@/stores/engineStore";
import { InstanceList } from "./InstanceList";
import { CreateInstanceDialog } from "@/components/integration/CreateInstanceDialog";
import type { DeployedProcess, DeployedNodeFlow } from "@/types/engine";

// ---------------------------------------------------------------------------
// ProcessList (container)
// ---------------------------------------------------------------------------

export function ProcessList() {
  const deployedProcesses = useEngineStore((s) => s.deployedProcesses);
  const deployedNodeFlows = useEngineStore((s) => s.deployedNodeFlows);

  const hasAny = deployedProcesses.length > 0 || deployedNodeFlows.length > 0;

  return (
    <div className="flex flex-col gap-0.5">
      {/* Deployed BPMN Processes section */}
      <SectionHeader label="Deployed BPMN" />
      {deployedProcesses.map((process, idx) => (
        <BPMNProcessItem key={`${process.processId}-${process.version}-${idx}`} process={process} />
      ))}
      {deployedProcesses.length === 0 && <EmptyMessage text="No processes deployed" />}

      {/* Deployed Node Flows section */}
      <SectionHeader label="Deployed Flows" className="mt-2" />
      {deployedNodeFlows.map((flow, idx) => (
        <NodeFlowItem key={`${flow.flowId}-${flow.version}-${idx}`} flow={flow} />
      ))}
      {deployedNodeFlows.length === 0 && <EmptyMessage text="No flows deployed" />}

      {/* Empty state */}
      {!hasAny && (
        <div className="px-3 py-2 text-center">
          <p className="text-xs text-zinc-600 italic">No deployments yet</p>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// BPMN Process Item
// ---------------------------------------------------------------------------

function BPMNProcessItem({ process }: { process: DeployedProcess }) {
  const [expanded, setExpanded] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  const instances = useEngineStore((s) =>
    s.runningInstances.filter((i) => i.processId === process.processId),
  );

  const activeCount = instances.filter((i) => i.state === "running").length;

  return (
    <div>
      <div className="flex w-full items-center gap-1.5 px-2 py-1 text-sm hover:bg-zinc-800 rounded group">
        {/* Expand/collapse button */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1.5 flex-1 min-w-0 text-left"
        >
          <ChevronRight
            size={14}
            className={[
              "text-zinc-500 transition-transform flex-shrink-0",
              expanded ? "rotate-90" : "",
            ].join(" ")}
          />
          <GitBranch size={14} className="text-blue-400 flex-shrink-0" />
          <span className="flex-1 truncate text-zinc-200">
            {process.resourceName.replace(/\.(bpmn|xml)$/i, "")}
          </span>
        </button>

        {/* Version badge */}
        <span className="text-xs text-zinc-500 flex-shrink-0 tabular-nums">v{process.version}</span>

        {/* Instance count */}
        <span className="text-xs text-zinc-500 flex-shrink-0 tabular-nums min-w-[16px] text-right">
          {instances.length > 0 && (
            <span title={`${instances.length} instance(s), ${activeCount} active`}>
              {instances.length}
            </span>
          )}
        </span>

        {/* Quick run button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowCreateDialog(true);
          }}
          className="rounded p-0.5 text-zinc-500 hover:text-green-400 hover:bg-zinc-700 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
          title="Start new instance"
        >
          <Play size={12} />
        </button>
      </div>

      {/* Expanded instance list */}
      {expanded && <InstanceList instances={instances} />}

      {/* Create Instance Dialog */}
      <CreateInstanceDialog
        processId={process.processId}
        processName={process.resourceName.replace(/\.(bpmn|xml)$/i, "")}
        engineType="bpmn"
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Node Flow Item
// ---------------------------------------------------------------------------

function NodeFlowItem({ flow }: { flow: DeployedNodeFlow }) {
  const [expanded, setExpanded] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  const instances = useEngineStore((s) =>
    s.runningInstances.filter((i) => i.processId === flow.flowId),
  );

  const activeCount = instances.filter((i) => i.state === "running").length;

  return (
    <div>
      <div className="flex w-full items-center gap-1.5 px-2 py-1 text-sm hover:bg-zinc-800 rounded group">
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1.5 flex-1 min-w-0 text-left"
        >
          <ChevronRight
            size={14}
            className={[
              "text-zinc-500 transition-transform flex-shrink-0",
              expanded ? "rotate-90" : "",
            ].join(" ")}
          />
          <FileCode size={14} className="text-green-400 flex-shrink-0" />
          <span className="flex-1 truncate text-zinc-200">
            {flow.resourceName.replace(/\.json$/i, "")}
          </span>
        </button>

        <span className="text-xs text-zinc-500 flex-shrink-0 tabular-nums">v{flow.version}</span>

        <span className="text-xs text-zinc-500 flex-shrink-0 tabular-nums min-w-[16px] text-right">
          {instances.length > 0 && (
            <span title={`${instances.length} instance(s), ${activeCount} active`}>
              {instances.length}
            </span>
          )}
        </span>

        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowCreateDialog(true);
          }}
          className="rounded p-0.5 text-zinc-500 hover:text-green-400 hover:bg-zinc-700 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
          title="Start new instance"
        >
          <Play size={12} />
        </button>
      </div>

      {expanded && <InstanceList instances={instances} />}

      <CreateInstanceDialog
        processId={flow.flowId}
        processName={flow.resourceName.replace(/\.json$/i, "")}
        engineType="nodeflow"
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared sub-components
// ---------------------------------------------------------------------------

function SectionHeader({ label, className }: { label: string; className?: string }) {
  return (
    <div
      className={[
        "px-3 py-1.5 text-xs font-semibold text-zinc-500 uppercase tracking-wider",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {label}
    </div>
  );
}

function EmptyMessage({ text }: { text: string }) {
  return <div className="px-3 py-2 text-xs text-zinc-600 italic">{text}</div>;
}
